import React, { useEffect, useState } from "react";
import { Users, Clock, ClipboardList, Heart, MessageSquare, Search, RefreshCcw, ChevronDown, ChevronRight, FileDown, Inbox } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { useApp } from "../../context/AppContext";
import { downloadReservationPdf } from "../../lib/pdf";

const fmtMinutes = (sec) => {
    const s = Math.max(0, Number(sec) || 0);
    if (s < 60) return `${s} sn`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} dk`;
    const h = Math.floor(m / 60);
    return `${h} sa ${m % 60} dk`;
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("tr-TR"); }
    catch { return iso; }
};

const STATUS_BADGE = {
    approved: { label: "ONAYLI", cls: "bg-emerald-700/40 text-emerald-300 border-emerald-600/50" },
    pending: { label: "BEKLEMEDE", cls: "bg-yellow-700/30 text-yellow-300 border-yellow-600/50" },
    rejected: { label: "REDDEDİLDİ", cls: "bg-rose-700/30 text-rose-300 border-rose-600/50" },
};

const ReservationsDrawer = ({ customer, settings, formatPrice }) => {
    const [loading, setLoading] = useState(true);
    const [reservations, setReservations] = useState([]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get(`/admin/customers/${customer.id}/reservations`)
            .then(({ data }) => { if (alive) setReservations(data.reservations || []); })
            .catch(() => toast.error("Rezervasyonlar yüklenemedi"))
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [customer.id]);

    if (loading) return <div className="text-muted_green text-sm p-4">Yükleniyor…</div>;
    if (reservations.length === 0) {
        return (
            <div className="p-6 text-center border border-dashed border-border_green" data-testid="customer-no-reservations">
                <Inbox className="w-6 h-6 text-muted_green mx-auto mb-2" />
                <div className="text-sm text-muted_green">Bu üyenin henüz rezervasyonu bulunmuyor.</div>
            </div>
        );
    }
    return (
        <div className="space-y-3" data-testid={`customer-reservations-${customer.id}`}>
            {reservations.map((r) => {
                const badge = STATUS_BADGE[r.status] || { label: r.status?.toUpperCase(), cls: "bg-emerald_deep text-muted_green border-border_green" };
                const remaining = Math.max(0, (r.total_price || 0) - (r.deposit_amount || 0));
                return (
                    <div key={r.id} className="border border-border_green bg-emerald_deep/60 p-4" data-testid={`admin-cust-res-${r.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className={`px-2 py-0.5 text-[10px] tracking-[0.15em] border ${badge.cls}`}>{badge.label}</span>
                                    <span className="font-mono text-xs text-gold">{r.reservation_code}</span>
                                </div>
                                <div className="font-serif text-lg">{r.hotel_name}</div>
                                <div className="text-xs text-muted_green">
                                    {r.room_name} × {r.rooms_count} · {r.guests} kişi · {r.nights} gece
                                </div>
                                <div className="text-xs text-muted_green">
                                    {new Date(r.check_in).toLocaleDateString("tr-TR")} → {new Date(r.check_out).toLocaleDateString("tr-TR")}
                                </div>
                            </div>
                            <button
                                onClick={() => downloadReservationPdf({ reservation: r, settings, formatPrice })}
                                className="btn-outline-gold !text-xs"
                                data-testid={`download-pdf-${r.id}`}
                            >
                                <FileDown className="w-3.5 h-3.5" /> PDF İndir
                            </button>
                        </div>
                        <div className="grid sm:grid-cols-3 gap-3 text-xs border-t border-border_green pt-3">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.15em] text-muted_green">Toplam</div>
                                <div className="text-bone font-semibold">{formatPrice ? formatPrice(r.total_price) : `${r.total_price} SAR`}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.15em] text-muted_green">Ön Ödeme</div>
                                <div className="text-bone">{formatPrice ? formatPrice(r.deposit_amount) : `${r.deposit_amount} SAR`}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.15em] text-gold">Otelde Kalan</div>
                                <div className="text-gold font-bold">{formatPrice ? formatPrice(remaining) : `${remaining} SAR`}</div>
                            </div>
                        </div>
                        <div className="text-[10px] text-muted_green mt-2">Talep: {fmtDate(r.created_at)}</div>
                    </div>
                );
            })}
        </div>
    );
};

const AdminCustomers = () => {
    const { settings, formatPrice } = useApp();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [expandedId, setExpandedId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/customers");
            setItems(data);
        } catch (e) {
            toast.error("Üyeler yüklenemedi");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const filtered = q
        ? items.filter((c) => `${c.name} ${c.email} ${c.phone}`.toLowerCase().includes(q.toLowerCase()))
        : items;

    const totals = filtered.reduce((acc, c) => ({
        res: acc.res + (c.reservations_total || 0),
        approved: acc.approved + (c.reservations_approved || 0),
        rev: acc.rev + (c.reviews_count || 0),
        fav: acc.fav + (c.favorites_count || 0),
        time: acc.time + (c.total_seconds || 0),
    }), { res: 0, approved: 0, rev: 0, fav: 0, time: 0 });

    const toggle = (id) => setExpandedId((cur) => (cur === id ? null : id));

    return (
        <div>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <h1 className="font-serif text-4xl mb-2">Üye Takip</h1>
                    <p className="text-muted_green text-sm">Üyelerin site aktivitelerini görüntüleyin, rezervasyonlarını inceleyin ve PDF makbuzlarını indirin.</p>
                </div>
                <button onClick={load} className="btn-outline-gold !text-xs" data-testid="refresh-customers"><RefreshCcw className="w-3.5 h-3.5" /> Yenile</button>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-border_green mb-6">
                {[
                    { l: "Toplam Üye", v: filtered.length, icon: Users },
                    { l: "Toplam Rezervasyon", v: totals.res, icon: ClipboardList },
                    { l: "Onaylı Rezervasyon", v: totals.approved, icon: ClipboardList },
                    { l: "Toplam Yorum", v: totals.rev, icon: MessageSquare },
                    { l: "Toplam Süre", v: fmtMinutes(totals.time), icon: Clock },
                ].map((s, i) => {
                    const Icon = s.icon;
                    return (
                        <div key={i} className="bg-emerald_surface p-5">
                            <Icon className="w-4 h-4 text-gold mb-2" />
                            <div className="font-serif text-2xl text-gold">{s.v}</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-muted_green mt-1">{s.l}</div>
                        </div>
                    );
                })}
            </div>

            <div className="mb-4 relative max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted_green" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="İsim, e-posta veya telefon ile ara..."
                    className="input-dark pl-10"
                    data-testid="customer-search"
                />
            </div>

            {loading ? (
                <div className="text-muted_green text-sm">Yükleniyor…</div>
            ) : filtered.length === 0 ? (
                <div className="border border-dashed border-border_green p-12 text-center text-sm text-muted_green">Üye bulunamadı.</div>
            ) : (
                <div className="border border-border_green overflow-x-auto">
                    <table className="w-full text-sm min-w-[760px]">
                        <thead className="bg-emerald_surface text-[10px] uppercase tracking-[0.18em] text-gold">
                            <tr>
                                <th className="w-10"></th>
                                <th className="text-left p-3">Üye</th>
                                <th className="text-left p-3">İletişim</th>
                                <th className="text-right p-3">Rezervasyon</th>
                                <th className="text-right p-3">Yorum</th>
                                <th className="text-right p-3"><Heart className="w-3 h-3 inline" /> Favori</th>
                                <th className="text-right p-3">Süre</th>
                                <th className="text-right p-3">Son Aktivite</th>
                            </tr>
                        </thead>
                        <tbody className="bg-emerald_deep/50 divide-y divide-border_green">
                            {filtered.map((c) => {
                                const open = expandedId === c.id;
                                const Chev = open ? ChevronDown : ChevronRight;
                                return (
                                    <React.Fragment key={c.id}>
                                        <tr
                                            className="hover:bg-emerald_hover/30 cursor-pointer"
                                            onClick={() => toggle(c.id)}
                                            data-testid={`customer-row-${c.id}`}
                                        >
                                            <td className="p-3 align-top">
                                                <Chev className="w-4 h-4 text-gold" />
                                            </td>
                                            <td className="p-3 align-top">
                                                <div className="font-serif text-base">{c.name}</div>
                                                <div className="text-[10px] text-muted_green">Üye: {fmtDate(c.created_at)}</div>
                                            </td>
                                            <td className="p-3 text-xs align-top">
                                                <div>{c.email}</div>
                                                <div className="text-muted_green">{c.phone || "—"}</div>
                                            </td>
                                            <td className="p-3 text-right align-top">
                                                <div className="text-gold font-semibold">{c.reservations_total}</div>
                                                <div className="text-[10px] text-emerald-400">{c.reservations_approved} onaylı</div>
                                            </td>
                                            <td className="p-3 text-right align-top text-gold font-semibold">{c.reviews_count}</td>
                                            <td className="p-3 text-right align-top text-gold font-semibold">{c.favorites_count}</td>
                                            <td className="p-3 text-right align-top text-xs">{fmtMinutes(c.total_seconds)}</td>
                                            <td className="p-3 text-right align-top text-[10px] text-muted_green">{fmtDate(c.last_seen)}</td>
                                        </tr>
                                        {open && (
                                            <tr className="bg-emerald_deep/80">
                                                <td colSpan={8} className="p-4">
                                                    <ReservationsDrawer customer={c} settings={settings} formatPrice={formatPrice} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminCustomers;
